import { TransferMultiModal } from "@components/common/multiselect/TransferMultiModal";
import { DSInput } from "@components/design-system/molecules";
import {
  faHourglass,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import { faNoteMedical } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFindAndReplace, useTeam } from "@hooks";
import { useDisplayName } from "@hooks/use-display-name";
import { Tooltip } from "@mui/material";
import { useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useLocation } from "react-router-dom";

import AutosaveState from "./AutosaveState";
import NoteActionsToolbar from "./NoteActionsToolbar";
import { NoteCreatedAtTimer } from "./NoteCreatedAtTimer";
import { AudioPlayer } from "../../../notebook/note/AudioPlayer";
import NoteStatus from "../../../notebook/note/NoteStatus";
import PatientTitle from "../../../notebook/PatientTitle";
import { NoteQualityPill } from "../NoteQualityPill";

function Divider({ children }) {
  return (
    <div className="justify-self-center sticky top-16 z-20  w-full my-5 bg-primary/90 backdrop-filter backdrop-blur-xs">
      {children}
    </div>
  );
}

export function ExistingNoteFields({
  note = null,
  refetchNote,
  isScribeEdit = false,
  isSuperSOAP,
  control,
  onSaveAsDraft,
  onSubmitNote,
}) {
  const [isTitleFocused, setIsTitleFocused] = useState(false);
  const titleRef = useRef(null);
  const location = useLocation();
  const { currentTeam } = useTeam();
  const isOnboarding = location.pathname === "/dashboard";

  const [isTransferModalOpen, setIsTransferModalOpen] =
    useState(false);
  const { selectFindAndReplaceText } = useFindAndReplace();

  const firstName = note?.user?.firstName;
  const lastName = note?.user?.lastName;

  const ownerDisplayName = useDisplayName(note?.user, {
    includeLastName: true,
  });

  if (note === null) {
    return null;
  }
  return (
    <>
      <Divider>
        <div className="flex flex-row items-center my-1 space-x-4">
          <div className="flex flex-1 flex-col md:flex-row md:items-center md:space-x-3">
            <div className="flex flex-row items-center space-x-3">
              <h2 className="text-left flex-1 text-primary">
                {note?.isMadeWithScribephone ? (
                  <FontAwesomeIcon
                    icon={faPhone}
                    size="1x"
                    className="mr-[6px] mb-[2px] text-green-500"
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faNoteMedical}
                    size="1x"
                    className="mr-[6px] mb-[2px] text-indigo-500"
                  />
                )}
                {note?.patient ? (
                  <>
                    Note for{" "}
                    <PatientTitle
                      sex={note?.patient?.sex}
                      species={note?.patient?.species}
                      name={note?.patient?.name}
                      patientUUID={note?.patient?.uuid}
                      lastNameOfOwner={note?.patient?.lastNameOfOwner}
                      size="lg"
                    />
                  </>
                ) : note?.jobTypeName ? (
                  note?.jobTypeName
                ) : (
                  "Quicknote"
                )}
              </h2>

              <div className="flex flex-row flex-wrap gap-1">
                <NoteStatus note={note} />
                <NoteQualityPill note={note} />
              </div>
            </div>

            <div className="flex-1 h-0.5 rounded-full "></div>
            <div className="flex flex-row items-center space-x-sm divide-x my-2 md:my-0">
              {note?.isAutoSoap && (
                <p className="px-sm m-0 text-secondary">
                  <FontAwesomeIcon
                    className="mr-1"
                    icon={faHourglass}
                    size="sm"
                  />
                  {note?.timeToProcess
                    ? `Processed in ${note?.timeToProcess}s`
                    : "Processing time unavailable"}
                </p>
              )}
              {!isScribeEdit && !isOnboarding && (
                <p className="px-sm m-0 text-secondary">
                  <AutosaveState
                    dataTestIdSaving="autosave-saving"
                    dataTestIdSaved="autosave-saved"
                  />
                </p>
              )}
              {note?.user && !note?.patient && currentTeam?.uuid && (
                <p
                  className="px-sm m-0 text-secondary"
                  data-testid={`note-owner-${firstName}-${lastName}`}
                >
                  {`${ownerDisplayName}'s Note`}
                </p>
              )}
            </div>
          </div>
        </div>

        <div>
          <AudioPlayer
            noteUuid={note?.uuid}
            isTagged={note?.isTagged}
            shouldShowAddButton={!isSuperSOAP}
            dataTestId="append-button"
          />
        </div>
        <div className="flex flex-row items-start space-x-2 mb-12 mt-3 w-full">
          <Tooltip title={note?.title} arrow placement="top">
            <div className="w-full flex-1">
              <Controller
                render={({ field }) => (
                  <DSInput
                    ref={titleRef}
                    placeholder={"Note Title (optional)"}
                    defaultValue={note?.title}
                    dataTestId="note-title-input"
                    {...field}
                    onFocus={() => setIsTitleFocused(true)}
                    onBlur={() => setIsTitleFocused(false)}
                    onMouseUp={(e) => {
                      selectFindAndReplaceText(e);
                    }}
                  />
                )}
                control={control}
                name={"title"}
              />
            </div>
          </Tooltip>

          <Tooltip
            title="Type in a new date or click the calendar icon to edit the date and time the note was made."
            arrow
            placement="top"
          >
            <div className="lg:min-w-xl grow-0">
              <NoteCreatedAtTimer />
            </div>
          </Tooltip>
          {!isScribeEdit && !isOnboarding && (
            <NoteActionsToolbar
              isAutoNote={note?.isAutoSoap}
              note={note}
              refetchNote={refetchNote}
              onSaveAsDraft={onSaveAsDraft}
              onSubmitNote={onSubmitNote}
              isTitleFocused={isTitleFocused}
            />
          )}
        </div>
      </Divider>

      <TransferMultiModal
        notes={[note]}
        shouldShowTransferModal={isTransferModalOpen}
        setShouldShowTransferModal={setIsTransferModalOpen}
        refetch={refetchNote}
        bypassContext={true}
      />
    </>
  );
}
